import React, { createContext, useContext, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { usePunchupHorizontalScroll, PunchupHorizontalScroll } from 'features/common/hooks/usePunchupHorizontalScroll';
import styles from './PunchupTable.module.css';

// Horizontal-scroll state shared from PunchupTableContainer down to PunchupActionsHeaderCell, which
// renders the overflow chevrons in the sticky top-right header cell. Defaults to a no-op shape so the
// header cell is safe to render outside a container (e.g. in isolation tests).
const NOOP_SCROLL: PunchupHorizontalScroll = {
	canScrollLeft: false,
	canScrollRight: false,
	scrollLeft: () => undefined,
	scrollRight: () => undefined,
};

const PunchupTableScrollContext = createContext<PunchupHorizontalScroll>(NOOP_SCROLL);

/** Read the enclosing table's horizontal-scroll state. Returns the no-op shape outside a
 * PunchupTableContainer. Consumed by PunchupActionsHeaderCell to render the overflow chevrons. */
export const usePunchupTableScroll = (): PunchupHorizontalScroll => useContext(PunchupTableScrollContext);

/* Fallback when DOM layout is not computed (e.g., jsdom in test environments);
 * value is arbitrary but must exceed the 60px floor. */
const DEFAULT_START_WIDTH_PX = 120;

/* Fires once for browsers without `scrollend` — see CLAUDE.md. */
const SCROLLEND_FALLBACK_MS = 500;

interface TableContainerProps {
	className?: string;
	style?: React.CSSProperties;
	/**
	 * CSS `grid-template-columns` for the entire table. Applied as an inline
	 * style on the inner `<table>` element. The table is the single source of
	 * column tracks; `<thead>`, `<tbody>`, and each `<tr>` participate via
	 * subgrid (see PunchupTable.module.css and CLAUDE.md → "Column width
	 * strategy"). Default per data column is `max-content`; user-resized
	 * columns become a fixed `<px>` width and truncate cell content via
	 * ellipsis below their natural fit.
	 */
	columns?: string;
	children?: React.ReactNode;
	/**
	 * When true, a dim-white veil + centred spinner covers the table while an already-loaded
	 * table re-fetches (manual Refresh / auto-poll). Rows stay in place underneath
	 * (PunchupTable invariant 10) — the veil is a visual "updating" cue only and does not block
	 * interaction. Pass `isFetching && !isLoading && !isFetchingNextPage`: the initial load shows
	 * the centred PunchupTableLoadingRow and pagination shows the load-more spinner instead.
	 */
	isRefetching?: boolean;
}

interface RowProps {
	style?: React.CSSProperties;
	className?: string;
	children?: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLTableRowElement>;
	/**
	 * Enables the two-step row-click UX:
	 *   1. Smooth-scrolls the nearest [data-punchup-scroll] ancestor to the rightmost position.
	 *   2. Once the scroll settles, clicks [data-actions-trigger] inside the row (if present).
	 * If no trigger exists in the row, the click has no visible effect.
	 * `onClick` (if also provided) fires after the trigger click.
	 */
	rowClick?: boolean;
}

interface ColumnHeaderProps {
	className?: string;
	/** Adds cursor-pointer and hover-bold behaviour; renders sort direction indicator. */
	sortable?: boolean;
	/** Active sort direction for this column. Null/undefined = column not currently sorted. */
	sortDirection?: 'asc' | 'desc' | null;
	onSort?: () => void;
	/** Called with the new absolute pixel width when the user drags the right-edge resize handle. */
	onResize?: (newWidth: number) => void;
	children?: React.ReactNode;
}

interface CellProps {
	className?: string;
	style?: React.CSSProperties;
	title?: string;
	children?: React.ReactNode;
}

// ── Container ────────────────────────────────────────────────────────────────

export function PunchupTableContainer({ className, style, columns, children, isRefetching }: TableContainerProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const scroll = usePunchupHorizontalScroll(scrollRef);
	return (
		<PunchupTableScrollContext.Provider value={scroll}>
			<div className={[styles.tableContainer, className].filter(Boolean).join(' ')} style={style}>
				<div className={styles.tableScroll} data-punchup-scroll ref={scrollRef}>
					<table style={columns ? { gridTemplateColumns: columns } : undefined}>{children}</table>
				</div>
				{isRefetching && (
					<div className={styles.refetchOverlay} role="progressbar" aria-label="Loading">
						{/* The overlay is the single labelled progressbar; PunchupSpinner has its own
                role="status" aria-label="Loading", so hide it from the a11y tree to avoid a
                duplicate "Loading" announcement. size 32 matches PunchupTableLoadingRow so the
                re-fetch spinner is identical to the first-load one. */}
						<span aria-hidden="true">
							<PunchupSpinner size={32} />
						</span>
					</div>
				)}
			</div>
		</PunchupTableScrollContext.Provider>
	);
}

// ── Rows ──────────────────────────────────────────────────────────────────────

export function PunchupHeaderRow({ style, className, children }: RowProps) {
	return (
		<tr className={[styles.headerRow, className].filter(Boolean).join(' ')} style={style}>
			{children}
		</tr>
	);
}

export function PunchupDataRow({ style, className, children, onClick, rowClick }: RowProps) {
	const handleClick: React.MouseEventHandler<HTMLTableRowElement> = (e) => {
		if (!rowClick && !onClick) return;

		if (rowClick) {
			const row = e.currentTarget;
			const trigger = row.querySelector<HTMLElement>('[data-actions-trigger]');
			const scrollEl = row.closest<HTMLElement>('[data-punchup-scroll]');

			const fire = (() => {
				let done = false;
				return () => {
					if (done) return;
					done = true;
					trigger?.click();
					onClick?.(e);
				};
			})();

			if (trigger && scrollEl) {
				const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
				const alreadyAtEnd = scrollEl.scrollLeft >= maxScroll - 1; /* 1px sub-pixel tolerance */
				if (maxScroll > 0 && !alreadyAtEnd) {
					scrollEl.scrollTo({ left: scrollEl.scrollWidth, behavior: 'smooth' });
					scrollEl.addEventListener('scrollend', fire, { once: true });
					setTimeout(fire, SCROLLEND_FALLBACK_MS);
					return;
				}
			}

			trigger?.click();
			onClick?.(e);
		} else {
			onClick?.(e);
		}
	};

	return (
		<tr
			className={[styles.dataRow, onClick || rowClick ? styles.dataRowClickable : null, className]
				.filter(Boolean)
				.join(' ')}
			style={style}
			onClick={handleClick}
		>
			{children}
		</tr>
	);
}

// ── Header cells ──────────────────────────────────────────────────────────────

export function PunchupColumnHeader({
	className,
	sortable,
	sortDirection,
	onSort,
	onResize,
	children,
}: ColumnHeaderProps) {
	const thRef = useRef<HTMLTableCellElement>(null);
	const [isResizing, setIsResizing] = useState(false);

	const handleResizeStart = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const startX = e.clientX;
		const startWidth = thRef.current?.offsetWidth ?? DEFAULT_START_WIDTH_PX;
		/* floor = header's natural scrollWidth; 60px jsdom guard — see CLAUDE.md "Resize floor" */
		const naturalFloor = Math.max(60, thRef.current?.scrollWidth ?? 0);
		setIsResizing(true);

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const newWidth = Math.max(naturalFloor, startWidth + (moveEvent.clientX - startX));
			onResize?.(newWidth);
		};

		const handleMouseUp = () => {
			setIsResizing(false);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	return (
		<th ref={thRef} className={[styles.columnHeader, className].filter(Boolean).join(' ')}>
			{sortable ? (
				<span
					className={styles.sortableLabel}
					onClick={onSort}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => e.key === 'Enter' && onSort?.()}
				>
					{children}
					{sortDirection === 'desc' && <span aria-hidden="true">↑</span>}
					{sortDirection === 'asc' && <span aria-hidden="true">↓</span>}
					{!sortDirection && (
						<svg className={styles.sortIconNeutral} aria-hidden="true" viewBox="0 0 8 12" fill="currentColor">
							<path d="M4 1L1 5h6L4 1z" />
							<path d="M4 11l3-4H1l3 4z" />
						</svg>
					)}
				</span>
			) : (
				children
			)}
			{onResize && (
				<span
					className={[styles.resizeHandle, isResizing ? styles.resizeHandleActive : null].filter(Boolean).join(' ')}
					onMouseDown={handleResizeStart}
					aria-hidden="true"
				/>
			)}
		</th>
	);
}

// ── Empty state row ───────────────────────────────────────────────────────────

/**
 * Full-width empty state row. Spans every subgrid column so the single <td>
 * stretches the full table width.
 */
export function PunchupTableEmptyRow({ text = 'No results found' }: { text?: string }) {
	return (
		<tr className={styles.emptyRow}>
			<td className={styles.emptyCell}>{text}</td>
		</tr>
	);
}

// ── Loading state row ─────────────────────────────────────────────────────────

/**
 * Full-width loading row. Used while the initial table fetch is in flight —
 * once data arrives (even if empty) the consumer should switch to
 * `PunchupTableEmptyRow`. The spinner uses the orange-arc `PunchupSpinner`
 * primitive to match the loading affordance on the Analytics charts.
 */
export function PunchupTableLoadingRow({ size = 32 }: { size?: number }) {
	return (
		<tr className={styles.loadingRow}>
			<td className={styles.loadingCell}>
				<PunchupSpinner size={size} />
			</td>
		</tr>
	);
}

// ── Typed data cells ──────────────────────────────────────────────────────────

/**
 * First-column cell: date/time data.
 * Applies tabular-nums and nowrap for clean number alignment.
 * Convention: always the leftmost column in the grid.
 */
export function PunchupDateTimeCell({ className, style, title, children }: CellProps) {
	return (
		<td className={[styles.dateTimeCell, className].filter(Boolean).join(' ')} style={style} title={title}>
			{children}
		</td>
	);
}

/**
 * Second-column cell: main entity name (show, order, ticket).
 * Medium weight, heading colour, with text truncation.
 * Convention: always the second column in the grid.
 */
export function PunchupEntityCell({ className, style, title, children }: CellProps) {
	return (
		<td className={[styles.entityCell, className].filter(Boolean).join(' ')} style={style} title={title}>
			{children}
		</td>
	);
}

/**
 * Third-column group: supporting name fields (comedian, ticket type, tag).
 * Body colour, with text truncation.
 * Convention: follows the entity column; multiple name columns allowed.
 */
export function PunchupNameCell({ className, style, title, children }: CellProps) {
	return (
		<td className={[styles.nameCell, className].filter(Boolean).join(' ')} style={style} title={title}>
			{children}
		</td>
	);
}

/**
 * Multi-line breakdown cell: stacks several short lines (e.g. a per-ticket-type
 * count + price list via `PunchupLineItemList`) instead of one truncated line.
 * Overrides the default single-line `.dataRow td` truncation (nowrap + ellipsis)
 * so every line renders in full and the row grows taller to fit. Use for the
 * ticket-type / add-ons columns that show a breakdown rather than a single value.
 */
export function PunchupBreakdownCell({ className, style, title, children }: CellProps) {
	return (
		<td className={[styles.breakdownCell, className].filter(Boolean).join(' ')} style={style} title={title}>
			{children}
		</td>
	);
}

interface ActionsCellProps {
	className?: string;
	children?: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLTableCellElement>;
}

/**
 * Sticky right-edge cell for row action menus (⋮ button, AttendeeActions, etc.).
 * Overrides the default .dataRow td truncation so menu content is never clipped.
 * Convention: always the last column; pair with actionsHeaderCell on the <th>.
 */
export function PunchupActionsCell({ className, children, onClick }: ActionsCellProps) {
	return (
		<td className={[styles.actionsCell, className].filter(Boolean).join(' ')} onClick={onClick}>
			{children}
		</td>
	);
}

interface ActionsHeaderCellProps {
	className?: string;
	children?: React.ReactNode;
}

/**
 * Sticky right-edge <th> that pairs with PunchupActionsCell on the header row.
 * Without it the actions column's header scrolls away on horizontal scroll while
 * the body cells stay pinned, leaving a gap at the top-right. The typed twin of
 * PunchupActionsCell — use it instead of a raw `<th className={punchupTableStyles.actionsHeaderCell} />`
 * so the sticky-header role can't be forgotten.
 *
 * When the table overflows horizontally it fills this otherwise-empty sticky cell with the
 * left/right column-scroll chevrons (fed by PunchupTableContainer's scroll context); each chevron is
 * disabled at its edge and the whole control is hidden when the table fits. Explicit `children`, when
 * passed, take precedence over the chevrons.
 */
export function PunchupActionsHeaderCell({ className, children }: ActionsHeaderCellProps) {
	const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } = usePunchupTableScroll();
	const showScrollControls = !children && (canScrollLeft || canScrollRight);
	return (
		<th className={[styles.actionsHeaderCell, className].filter(Boolean).join(' ')}>
			{showScrollControls ? (
				<div className={styles.scrollControls}>
					<button
						type="button"
						className={styles.scrollChevron}
						onClick={scrollLeft}
						disabled={!canScrollLeft}
						aria-label="Scroll columns left"
					>
						<ChevronLeft size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						className={styles.scrollChevron}
						onClick={scrollRight}
						disabled={!canScrollRight}
						aria-label="Scroll columns right"
					>
						<ChevronRight size={16} aria-hidden="true" />
					</button>
				</div>
			) : (
				children
			)}
		</th>
	);
}
